import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronDown, UserRound } from "lucide-react";
import { FEATURE_ACCOMPAGNEMENT, FEATURE_CORRESPONDANCE } from "../config/features";

// "Les programmes" est une catégorie évolutive : pour l'instant un seul programme
// (les 52 rendez-vous), mais d'autres viendront. On l'expose donc en dropdown.
const programmesChildren = [
  { to: "/programme", label: "Les 52 rendez-vous" },
];

const links = [
  { to: "/club-de-lettres", label: "Club de Lettres" },
  { type: "dropdown", key: "programmes", label: "Les programmes", basePath: "/programme", children: programmesChildren },
  ...(FEATURE_CORRESPONDANCE ? [{ to: "/correspondance", label: "Correspondance" }] : []),
  { to: "/ateliers", label: "Ateliers" },
  { to: "/journal", label: "Journal" },
  ...(FEATURE_ACCOMPAGNEMENT ? [{ to: "/accompagnement", label: "Accompagnement" }] : []),
  { to: "/a-propos", label: "À propos" },
  { to: "/carte-cadeau", label: "Offrir" },
  { to: "/contact", label: "Contact" },
];

function DropdownDesktop({ item }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const justClickedRef = useRef(false);
  const location = useLocation();

  function schedClose() {
    // If the user just clicked to open, don't auto-close on mouseleave.
    if (justClickedRef.current) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  }
  function keepOpen() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  }
  function toggleClick() {
    setOpen(v => !v);
    justClickedRef.current = true;
    setTimeout(() => { justClickedRef.current = false; }, 400);
  }

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close when route changes (after clicking a child link)
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = location.pathname.startsWith(item.basePath);

  return (
    <div ref={ref} className="relative" onMouseEnter={keepOpen} onMouseLeave={schedClose}>
      <button
        type="button"
        data-testid="nav-programmes-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={toggleClick}
        onFocus={keepOpen}
        className={`relative text-[13px] tracking-wide whitespace-nowrap transition-colors py-1 inline-flex items-center gap-1 ${
          isActive ? "text-[#A3876A]" : "text-[#4A4036] hover:text-[#A3876A]"
        } after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-[#A3876A] after:origin-center after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100 ${isActive ? "after:scale-x-100" : ""}`}
      >
        {item.label}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.6}/>
      </button>
      {open && (
        <div
          data-testid="nav-programmes-menu"
          role="menu"
          className="absolute right-0 top-full mt-2 min-w-[220px] bg-[#FDFBF7] border border-[#E8DCC8] shadow-[0_10px_30px_-10px_rgba(44,38,33,0.18)] py-2"
        >
          {item.children.map(c => (
            <NavLink
              key={c.to}
              to={c.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-programmes-child-${c.to.replace(/\//g,"-")}`}
              className={({isActive: a}) =>
                `block px-5 py-2.5 text-sm ${a ? "text-[#A3876A] bg-[#F5F0E6]" : "text-[#4A4036] hover:bg-[#F5F0E6] hover:text-[#A3876A]"} transition-colors`
              }
              role="menuitem"
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  useEffect(() => { setOpen(false); }, []);
  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#E8DCC8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" aria-label="Les Rendez-vous de l'écriture — retour à l'accueil" className="flex items-center flex-shrink-0 group">
          <img
            src="/logo-header.png?v=1"
            alt="Les Rendez-vous de l'écriture"
            className="h-10 md:h-12 w-auto object-contain transition-opacity group-hover:opacity-85"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 flex-1 justify-end">
          {links.map(l => {
            if (l.type === "dropdown") return <DropdownDesktop key={l.key} item={l}/>;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/","home")}`}
                className={({isActive}) =>
                  `relative text-[13px] tracking-wide whitespace-nowrap transition-colors py-1 ${
                    isActive
                      ? "text-[#A3876A]"
                      : "text-[#4A4036] hover:text-[#A3876A]"
                  } after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-[#A3876A] after:origin-center after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100 aria-[current=page]:after:scale-x-100`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
          <NavLink
            to="/espace"
            data-testid="nav-espace"
            title="Mon espace"
            className={({isActive}) => `inline-flex items-center gap-1.5 text-[13px] tracking-wide whitespace-nowrap border px-3 py-1.5 transition-colors ${isActive ? "border-[#A3876A] text-[#A3876A]" : "border-[#E8DCC8] text-[#4A4036] hover:border-[#A3876A] hover:text-[#A3876A]"}`}
          >
            <UserRound size={14}/> Mon espace
          </NavLink>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          className="lg:hidden p-2 text-[#4A4036] hover:text-[#A3876A] transition-colors"
        >
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E8DCC8] bg-[#FDFBF7]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map(l => {
              if (l.type === "dropdown") {
                return (
                  <div key={l.key}>
                    <button
                      type="button"
                      data-testid="nav-mobile-programmes-toggle"
                      onClick={() => setProgOpen(v => !v)}
                      className="w-full flex items-center justify-between py-2 text-[#4A4036] hover:text-[#A3876A] transition-colors"
                    >
                      <span>{l.label}</span>
                      <ChevronDown size={16} className={`transition-transform ${progOpen ? "rotate-180" : ""}`} strokeWidth={1.6}/>
                    </button>
                    {progOpen && (
                      <div className="pl-4 border-l border-[#E8DCC8] mt-1 space-y-1">
                        {l.children.map(c => (
                          <Link
                            key={c.to}
                            to={c.to}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-programmes-child-${c.to.replace(/\//g,"-")}`}
                            className="block py-2 text-sm text-[#4A4036] hover:text-[#A3876A] transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-2 text-[#4A4036] hover:text-[#A3876A] transition-colors"
                >
                  {l.label}
                </Link>
              );
            })}
            <div className="pt-3 mt-2 border-t border-[#E8DCC8] flex gap-4 text-sm text-[#7C7064]">
              <Link to="/espace" data-testid="nav-mobile-espace" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 hover:text-[#A3876A]"><UserRound size={14}/> Mon espace</Link>
              <Link to="/faq" onClick={() => setOpen(false)} className="hover:text-[#A3876A]">FAQ</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
